const DAY_MS = 24 * 60 * 60 * 1000

/**
 * @typedef {object} AnonymousMoment
 * @property {string} ID
 * @property {string} Title
 * @property {number|null} Position
 * @property {number} submissions - Hoe vaak de vragenlijst is ingevuld (invullingen, geen documenten)
 * @property {number|null} lastAnswerMs - Wanneer het laatste antwoord binnenkwam
 *
 * @typedef {object} AnonymousResearchRow
 * @property {string} key - `Research.ID`
 * @property {string} researchId
 * @property {string} researchTitle
 * @property {string} questionnaireId - Voedt de deel-link
 * @property {Array<AnonymousMoment>} moments - Op volgorde; één kolom per stuk
 * @property {AnonymousMoment|null} next - Het eerste meetmoment zonder antwoorden
 * @property {number|null} silentSinceMs - Wanneer er voor het laatst íéts binnenkwam
 * @property {number|null} silentDays - Hoe lang het al stil is, in hele dagen
 * @property {number} totalSubmissions - Alle invullingen van dit onderzoek bij elkaar
 */

/**
 * Eén rij per onderzoek zónder herleidbare invullers — een groepsmeting.
 *
 * Bij zo'n onderzoek bestaat 'is Jan toe aan zijn tweede meting' niet: er is
 * geen Jan. Het enige dat te weten valt is hoe vaak de vragenlijst per meetmoment
 * is ingevuld, en wanneer er voor het laatst iets binnenkwam. Daarom telt deze
 * kaart in plaats van af te vinken, en telt hij stilte in plaats van af te
 * tellen naar een datum: zonder deelnemer is er geen startpunt en dus geen
 * termijn. Een verzonnen termijn is erger dan geen termijn.
 *
 * Een onderzoek hoort hier zodra het meetmomenten heeft, géén enkel antwoord op
 * naam draagt, en er iets te doen valt (minstens één meetmoment zonder
 * antwoorden). Met daarbij één afbakening tegen dubbeling: een onderzoek zonder
 * antwoorden dat wél aan deelnemers gekoppeld is, staat al volledig op de kaart
 * 'Aankomende meetmomenten' — dat blijft daar.
 *
 * AI-CONTEXT: See docs/features/meetmoment-signalering.md
 *
 * @param {object} [args]
 * @param {Record<string, {ID: string, Title?: string, QuestionnaireID?: string}>} [args.researchById]
 * @param {Record<string, Array<{ID: string, Title?: string, Position?: number}>>} [args.momentsByResearch]
 * @param {Record<string, import('./deriveResearchAggregate').ResearchAggregate|null>} [args.aggregateByResearch]
 *   Per onderzoek de afgeleide set. `null` betekent dat de antwoorden niet gelezen konden
 *   worden — die onderzoeken vallen weg, want nul tonen zou een bewering zijn.
 * @param {Array<string>} [args.linkedResearchIds] - Onderzoeken die minstens één deelnemer
 *   of huishouden bereiken
 * @param {number} [args.now] - Nu, in milliseconden
 * @returns {Array<AnonymousResearchRow>}
 */
const buildAnonymousResearchRows = ({
    researchById,
    momentsByResearch,
    aggregateByResearch,
    linkedResearchIds,
    now,
} = {}) => {
    const researches = researchById || {}
    const momentsByResearchId = momentsByResearch || {}
    const aggregates = aggregateByResearch || {}
    const linked = new Set(linkedResearchIds || [])
    const nowMs = typeof now === 'number' ? now : Date.now()

    const rows = []

    Object.keys(researches).forEach((researchId) => {
        const research = researches[researchId]
        if (!research) return

        const rawMoments = momentsByResearchId[researchId]
        if (!Array.isArray(rawMoments) || rawMoments.length === 0) return

        const aggregate = aggregates[researchId]
        if (!aggregate) return
        if (aggregate.hasIdentifiedAnswers) return

        // Een meetmoment zonder Position achteraan, niet weglaten — een
        // ontbrekend sorteerveld mag geen kolom doen verdwijnen.
        const ordered = [...rawMoments].sort(
            (a, b) => (a?.Position ?? Number.MAX_SAFE_INTEGER) - (b?.Position ?? Number.MAX_SAFE_INTEGER)
        )

        const moments = ordered.map((moment) => ({
            ID: moment.ID,
            Title: moment.Title || '',
            Position: moment.Position ?? null,
            submissions: aggregate.submissionsByMoment?.[moment.ID] || 0,
            lastAnswerMs: aggregate.lastAnswerByMoment?.[moment.ID] ?? null,
        }))

        const totalSubmissions = moments.reduce((sum, moment) => sum + moment.submissions, 0)

        // Niets meer te doen: elk meetmoment is minstens één keer ingevuld.
        const next = moments.find((moment) => moment.submissions === 0) || null
        if (next === null) return

        // Nooit iets binnengekregen én gekoppeld aan deelnemers: die meting
        // staat al op de andere kaart, per persoon. Hier zou hij dubbelen.
        if (totalSubmissions === 0 && linked.has(researchId)) return

        // Sinds wanneer is het stil? Het MEEST RECENTE antwoord van het hele
        // onderzoek telt, niet dat van het meetmoment ervóór: een groepsmeting
        // loopt niet netjes op volgorde, en dan is 'al 62 dagen niets' onwaar
        // terwijl er vorige week nog iets binnenkwam.
        const silentSinceMs = moments.reduce((latest, moment) => (
            moment.lastAnswerMs !== null && (latest === null || moment.lastAnswerMs > latest)
                ? moment.lastAnswerMs
                : latest
        ), /** @type {number|null} */ (null))

        rows.push({
            key: researchId,
            researchId,
            researchTitle: research.Title || '',
            questionnaireId: research.QuestionnaireID || '',
            moments,
            next,
            silentSinceMs,
            silentDays: silentSinceMs === null
                ? null
                : Math.max(0, Math.floor((nowMs - silentSinceMs) / DAY_MS)),
            totalSubmissions,
        })
    })

    return rows
}

export default buildAnonymousResearchRows
